import { PackageManagementList } from '@/pages/packages/PackageManagementList';

export function PackagesPage() {
  return <PackageManagementList />;
}

export default PackagesPage;
/*
import { PackageManagementList } from '@/pages/packages/PackageManagementList';

export function PackagesPage() {
  return <PackageManagementList />;
}

export default PackagesPage;
import { PackageManagementList } from '@/pages/packages/PackageManagementList';

export function PackagesPage() {
  return <PackageManagementList />;
}

export default PackagesPage;
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePatientNavigation } from '@/components/ui/PatientLink';
import { usePageContext } from '@/hooks/usePageContext';
import { RefreshCw } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { useEffectiveFacilityId, useIsFacilityLocked } from '@/hooks/useGlobalFacility';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import type { BadgeVariant, SelectOption } from '@/types/common.types';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { getPatientPackages, type PatientPackage } from '@/services/packages.service';
import {
  getPackageManagementOptions,
  getPackageManagementRows,
  type ManagementSearchType,
  type PackageFilterStatus,
  type PackageManagementFilters,
  type PackageManagementRow,
} from '@/services/packages-management.service';

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search packages...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'package', label: 'Package', type: 'select', allLabel: 'All packages' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses' },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities' },
];

const SEARCH_OPTIONS: SelectOption<ManagementSearchType>[] = [
  { value: '0', label: 'Name' },
  { value: '1', label: 'Plan ID' },
  { value: '2', label: 'Patient ID' },
  { value: '5', label: 'Pymt. ID' },
  { value: '3', label: 'Amount' },
  { value: '4', label: 'User' },
];

const STATUS_OPTIONS: SelectOption<PackageFilterStatus>[] = [
  { value: 'active', label: 'Active' },
  { value: 'declined', label: 'Declined' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'frozen', label: 'Frozen' },
];

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  active: 'success',
  declined: 'warning',
  inactive: 'neutral',
  frozen: 'info',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '\u2014';
  if (value === '0000-00-00' || value === '0000-00-00 00:00:00') return '\u2014';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function statusLabel(status: string): string {
  switch (status) {
    case 'declined':
      return 'Declined';
    case 'inactive':
      return 'Inactive';
    case 'frozen':
      return 'Frozen';
    default:
      return 'Active';
  }
}

function PackageDetailModal({
  row,
  onClose,
}: {
  row: PackageManagementRow;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['patient-package-detail', row.patientId],
    queryFn: () => getPatientPackages(row.patientId, { includeInactive: true }),
  });

  const packageDetail = useMemo<PatientPackage | undefined>(
    () => detailQuery.data?.find((item) => item.patientPackageId === row.patientPackageId),
    [detailQuery.data, row.patientPackageId],
  );

  return (
    <Modal isOpen onClose={onClose} title="Package Details" size="lg">
      {detailQuery.isLoading ? (
        <p className="py-8 text-center text-sm text-slate-500">Loading package details...</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Patient</p>
              <p className="text-sm font-medium text-slate-800">{row.patientName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Package</p>
              <p className="text-sm font-medium text-slate-800">{row.type}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Status</p>
              <Badge variant={STATUS_VARIANTS[row.status] ?? 'neutral'} dot>
                {statusLabel(row.status)}
              </Badge>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Amount</p>
              <p className="text-sm font-medium text-slate-800">{formatCurrency(row.amount)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Purchase Date</p>
              <p className="text-sm text-slate-700">{formatDate(row.createdAt)}</p>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Notes</p>
            <p className="text-sm text-slate-700">{row.notes || '\u2014'}</p>
          </div>

          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Items</p>
            {packageDetail?.items?.length ? (
              <div className="divide-y rounded-md border border-slate-200">
                {packageDetail.items.map((item) => (
                  <div key={item.patientPackageItemId} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{item.itemName}</p>
                      <p className="text-xs text-slate-400">
                        {item.usesConsumed} / {item.usesTotal} used
                      </p>
                    </div>
                    <span className="text-slate-600">{item.usesRemaining} remaining</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No package items found.</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export function PackagesPage() {
  const navToPatient = usePatientNavigation();
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Packages');

  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<Omit<PackageManagementFilters, 'search'> & { search?: string }>({
    page: 1,
    perPage: 25,
    sortBy: 'createdAt',
    sortDir: 'desc',
    searchType: '0',
  });
  const [detailRow, setDetailRow] = useState<PackageManagementRow | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setFilters((current) => ({
        ...current,
        page: 1,
        search: searchInput.trim() || undefined,
      }));
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const optionsQuery = useQuery({
    queryKey: ['package-management-options'],
    queryFn: getPackageManagementOptions,
    staleTime: 300_000,
  });

  const effectiveFacilityId = useEffectiveFacilityId(filters.facilityId);
  const isFacilityLocked = useIsFacilityLocked();
  const queryFilters = useMemo(
    () => ({ ...filters, facilityId: effectiveFacilityId }),
    [filters, effectiveFacilityId],
  );

  const listQuery = useQuery({
    queryKey: ['package-management', queryFilters],
    queryFn: () => getPackageManagementRows(queryFilters),
  });

  const result = listQuery.data;
  const stats = result?.stats ?? {
    activeCount: 0,
    declinedCount: 0,
    inactiveCount: 0,
    frozenCount: 0,
  };

  // Push page context to AI sidebar
  useEffect(() => {
    const filterParts: string[] = [];
    if (filters.status) filterParts.push(`Status: ${filters.status}`);
    if (filters.search) filterParts.push(`Search: "${filters.search}"`);
    if (filters.dateFrom || filters.dateTo) filterParts.push(`Date: ${filters.dateFrom ?? '...'} to ${filters.dateTo ?? '...'}`);
    updateAIContext({
      filters: filterParts.length > 0 ? filterParts.join(', ') : 'No filters applied',
      dataSummary: `${result?.pagination.total ?? 0} packages (${stats.activeCount} active, ${stats.declinedCount} declined, ${stats.inactiveCount} inactive, ${stats.frozenCount} frozen) — Page ${filters.page}`,
    });
  }, [filters, result?.pagination.total, stats, updateAIContext]);

  const templateOptions = useMemo<SelectOption<number>[]>(
    () => optionsQuery.data?.templates.map((item) => ({ value: item.value, label: item.label })) ?? [],
    [optionsQuery.data],
  );

  const facilityOptions = useMemo<SelectOption<number>[]>(
    () => optionsQuery.data?.facilities.map((item) => ({ value: item.value, label: item.label })) ?? [],
    [optionsQuery.data],
  );

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      package: templateOptions.map((item) => ({ value: String(item.value), label: item.label })),
      status: STATUS_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
      facility: facilityOptions.map((item) => ({ value: String(item.value), label: item.label })),
    }),
    [facilityOptions, templateOptions],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(
    () => ({
      search: filters.search ? [filters.search] : [],
      daterange: {
        from: filters.dateFrom ?? '',
        to: filters.dateTo ?? '',
      },
      package: filters.templateId ? [String(filters.templateId)] : [],
      status: filters.status ? [filters.status] : [],
      facility: effectiveFacilityId ? [String(effectiveFacilityId)] : [],
    }),
    [filters.search, filters.dateFrom, filters.dateTo, effectiveFacilityId, filters.status, filters.templateId],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    setFilters((current) => {
      switch (filterKey) {
        case 'search':
          setSearchInput(Array.isArray(value) && value[0] ? value[0] : '');
          return current;
        case 'daterange':
          if (Array.isArray(value) || !('from' in value)) return current;
          return {
            ...current,
            page: 1,
            dateFrom: value.from || undefined,
            dateTo: value.to || undefined,
          };
        case 'package':
          return {
            ...current,
            page: 1,
            templateId: Array.isArray(value) && value[0] ? Number(value[0]) : undefined,
          };
        case 'status':
          return {
            ...current,
            page: 1,
            status: Array.isArray(value) && value[0] ? (value[0] as PackageFilterStatus) : undefined,
          };
        case 'facility':
          return {
            ...current,
            page: 1,
            facilityId: Array.isArray(value) && value[0] ? Number(value[0]) : undefined,
          };
        default:
          return current;
      }
    });
  }, []);

  const handleSidebarClear = useCallback((filterKey: string) => {
    setFilters((current) => {
      switch (filterKey) {
        case 'search':
          setSearchInput('');
          return current;
        case 'daterange':
          return {
            ...current,
            page: 1,
            dateFrom: undefined,
            dateTo: undefined,
          };
        case 'package':
          return {
            ...current,
            page: 1,
            templateId: undefined,
          };
        case 'status':
          return {
            ...current,
            page: 1,
            status: undefined,
          };
        case 'facility':
          return {
            ...current,
            page: 1,
            facilityId: undefined,
          };
        default:
          return current;
      }
    });
  }, []);

  const columns = useMemo<DataTableColumn<PackageManagementRow>[]>(() => [
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-slate-800">{row.patientName}</p>
          <p className="text-xs text-slate-400">#{row.patientId}</p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (row) => <span className="text-slate-700">{row.type || '\u2014'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-800">{formatCurrency(row.amount)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <Badge variant={STATUS_VARIANTS[row.status] ?? 'neutral'} dot>
          {statusLabel(row.status)}
        </Badge>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      sortable: true,
      render: (row) => <span className="text-slate-600">{row.notes || '\u2014'}</span>,
    },
  ], []);

  const actions = useMemo<DataTableAction<PackageManagementRow>[]>(() => [
    {
      label: 'View Details',
      onClick: (row) => setDetailRow(row),
    },
    {
      label: 'View Patient',
      onClick: (row) => navToPatient({ patientId: row.patientId, name: row.patientName }),
    },
  ], [navToPatient]);

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Packages"
              actions={(
                <Button
                  variant="ghost"
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: ['package-management'] });
                    queryClient.invalidateQueries({ queryKey: ['package-management-options'] });
                  }}
                >
                  <RefreshCw className="mr-1 h-4 w-4" />
                  Refresh
                </Button>
              )}
            />

            <MetricCardGrid columns={4}>
              <StatCard label="Active" value={stats.activeCount} />
              <StatCard label="Declined" value={stats.declinedCount} />
              <StatCard label="Inactive" value={stats.inactiveCount} />
              <StatCard label="Frozen" value={stats.frozenCount} />
            </MetricCardGrid>

            <div className="flex flex-wrap items-center gap-3">
              <div className="w-40">
                <Select
                  options={SEARCH_OPTIONS}
                  value={filters.searchType}
                  onChange={(value) => setFilters((current) => ({
                    ...current,
                    page: 1,
                    searchType: value ?? '0',
                  }))}
                />
              </div>

            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<PackageManagementRow>
              data={result?.items ?? []}
              columns={columns}
              keyField="patientPackageId"
              loading={listQuery.isLoading}
              rowActions={actions}
              onRowClick={(row) => setDetailRow(row)}
              page={result?.pagination.page ?? filters.page}
              pageSize={result?.pagination.perPage ?? filters.perPage}
              total={result?.pagination.total ?? 0}
              onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
              manualSort
              sortKey={result?.sort.field ?? filters.sortBy}
              sortDir={result?.sort.direction ?? filters.sortDir}
              onSortChange={(key, direction) => setFilters((current) => ({
                ...current,
                page: 1,
                sortBy: key,
                sortDir: direction,
              }))}
              emptyTitle="No packages found"
              emptyDescription="Try adjusting the management filters."
            />
          </div>
        </div>
      </ListPageLayout>

      {detailRow && (
        <PackageDetailModal row={detailRow} onClose={() => setDetailRow(null)} />
      )}
    </>
  );
}

export default PackagesPage;
*/
